"use client";

import { useCallback, useEffect, useState } from "react";

/**
 * Server-side search config defaults, fetched from /api/v1/search/defaults.
 * Ground truth lives in src/configs/config.py — this is its typed mirror.
 * All percentage-style values are 0-100 integers (matching the admin UI scale).
 */
export interface SearchDefaults {
  search_top_k: number;
  candidate_pool_multiplier: number;
  candidate_pool_multiplier_max: number;
  quality_retrieval_boost: number;
  search_query_weight: number;
  search_quality_weight: number;
  linkedin_quality_company_weight: number;
  github_bonus_weight: number;
  github_only_quality_cap: number;
  company_history_decay: [number, number, number];
  school_history_decay: [number, number, number];
  company_age_decay: [number, number, number];
  company_unranked_weight: number;
  school_unranked_weight: number;
  company_tier_weights: Record<string, number>;
  school_tier_weights: Record<string, number>;
}

/**
 * Hardcoded fallback matching config.py at the time of writing.
 * Only used until /api/v1/search/defaults responds — never treated as ground truth.
 * Ground truth: src/configs/config.py → /api/v1/search/defaults endpoint.
 */
const FALLBACK: SearchDefaults = {
  search_top_k: 500,
  candidate_pool_multiplier: 5,
  candidate_pool_multiplier_max: 100,
  quality_retrieval_boost: 6,
  search_query_weight: 60,
  search_quality_weight: 40,
  linkedin_quality_company_weight: 60,
  github_bonus_weight: 20,
  github_only_quality_cap: 40,
  company_history_decay: [80, 15, 5],
  school_history_decay: [100, 0, 0],
  company_age_decay: [2, 6, 15],
  company_unranked_weight: 10,
  school_unranked_weight: 25,
  company_tier_weights: { "1": 100, "2": 50, "3": 10 },
  school_tier_weights: { "1": 100, "2": 50, "3": 25, "4": 10, "5": 5 },
};

/** Fallback top_k for non-hook contexts (e.g. cache warming). Prefer useSearchDefaults(). */
export const FALLBACK_SEARCH_TOP_K = FALLBACK.search_top_k;

let cached: SearchDefaults | null = null;
let inflight: Promise<SearchDefaults> | null = null;

/** Custom event dispatched after defaults are refetched from server. */
const DEFAULTS_CHANGED_EVENT = "search-defaults-changed";

async function fetchDefaults(): Promise<SearchDefaults> {
  try {
    const res = await fetch(`/api/v1/search/defaults?_t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return FALLBACK;
    const data = await res.json();
    cached = data as SearchDefaults;
    return cached;
  } catch {
    return FALLBACK;
  }
}

/**
 * Invalidate the in-memory defaults cache and re-fetch from the server.
 * Call this after saving new defaults so all components pick up the
 * updated values on their next render.
 */
export async function invalidateDefaults(): Promise<SearchDefaults> {
  cached = null;
  inflight = null;
  const fresh = await fetchDefaults();
  window.dispatchEvent(new Event(DEFAULTS_CHANGED_EVENT));
  return fresh;
}

export function useSearchDefaults(): { defaults: SearchDefaults; refetch: () => Promise<SearchDefaults> } {
  const [defaults, setDefaults] = useState<SearchDefaults>(cached ?? FALLBACK);

  useEffect(() => {
    if (cached) {
      setDefaults(cached);
      return;
    }
    if (!inflight) {
      inflight = fetchDefaults();
    }
    inflight.then((d) => setDefaults(d));
  }, []);

  // Re-sync when another component calls invalidateDefaults()
  useEffect(() => {
    function sync() {
      if (cached) setDefaults(cached);
    }
    window.addEventListener(DEFAULTS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(DEFAULTS_CHANGED_EVENT, sync);
  }, []);

  const refetch = useCallback(async () => {
    const fresh = await invalidateDefaults();
    setDefaults(fresh);
    return fresh;
  }, []);

  return { defaults, refetch };
}
